import React, { useEffect, useRef, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { statusColor } from "@/lib/constants";
import { Loader2, FlaskConical, Upload, FileText, MapPin } from "lucide-react";

export default function LabDashboard() {
  const [tests, setTests] = useState(null);
  const [busy, setBusy] = useState(null);
  const fileRefs = useRef({});

  const load = async () => setTests((await api.get("/lab/tests")).data.tests);
  useEffect(() => { load(); }, []);

  if (!tests) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  const uploadReport = async (t, file) => {
    if (!file) return;
    setBusy(t.id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/genetic/${t.id}/report`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success("Report uploaded — status is now Report Available");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(null); }
  };

  const fmtAddr = (a) => (a ? [a.line1, a.line2, a.city, a.state, a.postal_code, a.country].filter(Boolean).join(", ") : "—");
  const inLab = tests.filter((t) => t.status === "In Lab").length;

  return (
    <div>
      <div className="eyebrow">Lab partner queue</div>
      <h1 className="font-head text-3xl font-bold text-ink mt-1">Home-collection test queue</h1>
      <p className="text-ink-soft mt-1">Paid orders awaiting sample collection & report upload. DNA processing is handled externally.</p>

      <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#F5F3FF] border border-[#DDD6FE] px-3 py-1.5 text-sm font-semibold text-[#5B21B6]">
        <FlaskConical className="h-4 w-4" /> {inLab} awaiting report
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-left text-ink-muted">
            <tr>
              <th className="px-4 py-3 font-semibold">MANOGENE ID</th>
              <th className="px-4 py-3 font-semibold">Condition</th>
              <th className="px-4 py-3 font-semibold">Genes</th>
              <th className="px-4 py-3 font-semibold">Home collection address</th>
              <th className="px-4 py-3 font-semibold">Preferred date</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Report</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tests.map((t) => {
              const sc = statusColor(t.status);
              return (
                <tr key={t.id} className="hover:bg-slate-50 transition-colors" data-testid={`lab-row-${t.id}`}>
                  <td className="px-4 py-3 mono text-brand whitespace-nowrap">{t.patient?.manogene_id}</td>
                  <td className="px-4 py-3 font-medium text-ink">{t.condition?.name}</td>
                  <td className="px-4 py-3 text-ink-soft">{(t.recommended_tests || []).map((x) => x.gene).join(", ")}</td>
                  <td className="px-4 py-3 text-ink-soft max-w-[220px]">
                    <span className="inline-flex items-start gap-1"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-ink-muted" />{fmtAddr(t.address)}</span>
                  </td>
                  <td className="px-4 py-3 text-ink-soft whitespace-nowrap">{t.collection_date || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold mono"
                      style={{ background: sc.bg, color: sc.text, borderColor: sc.border }}>{t.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <input type="file" accept="application/pdf" className="hidden"
                      ref={(el) => (fileRefs.current[t.id] = el)}
                      onChange={(e) => uploadReport(t, e.target.files[0])} data-testid={`report-input-${t.id}`} />
                    {t.report_file_id ? (
                      <a href={`${API}/genetic/report/${t.report_file_id}?auth=${getToken()}`} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-brand text-xs font-semibold hover:underline">
                        <FileText className="h-3.5 w-3.5" /> View
                      </a>
                    ) : (
                      <button onClick={() => fileRefs.current[t.id]?.click()} disabled={busy === t.id} data-testid={`upload-${t.id}`}
                        className="inline-flex items-center gap-1 rounded-lg bg-brand text-white px-3 py-1.5 text-xs font-semibold hover:bg-brand-hover transition-colors disabled:opacity-50">
                        {busy === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload report PDF
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {tests.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-ink-muted">
                <FlaskConical className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                No paid orders in the queue yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
